import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'

import { ProviderTaskResponse } from '../../../providers/ProviderTask/ProviderTask.provider'

interface DeleteProviderTaskModalProps {
  loading: boolean
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  tasks: ProviderTaskResponse[]
  selectedTask: ProviderTaskResponse | null
  setSelectedTask: React.Dispatch<
    React.SetStateAction<ProviderTaskResponse | null>
  >
  showDeleteConfirm: boolean
  setShowDeleteConfirm: React.Dispatch<React.SetStateAction<boolean>>
  onDeleteTask: (providerTaskId: number) => Promise<any>
  setOptimisticTasks: React.Dispatch<
    React.SetStateAction<ProviderTaskResponse[]>
  >
}

export const DeleteProviderTaskModal = ({
  loading,
  setLoading,
  tasks,
  selectedTask,
  setSelectedTask,
  showDeleteConfirm,
  setShowDeleteConfirm,
  onDeleteTask,
  setOptimisticTasks,
}: DeleteProviderTaskModalProps) => {
  const handleDeleteTask = async () => {
    setLoading(true)

    if (!selectedTask) return

    const taskToDelete = selectedTask
    const previousIndex = tasks.findIndex((t) => t.id === taskToDelete.id)

    // Remove immediately, close dialog right away
    setOptimisticTasks((prev) => prev.filter((t) => t.id !== taskToDelete.id))
    setShowDeleteConfirm(false)
    setSelectedTask(null)

    try {
      await onDeleteTask(taskToDelete.id)
    } catch (error) {
      console.log('error', error)
      // Rollback — reinsert at its original position
      setOptimisticTasks((prev) => {
        const next = [...prev]
        const insertAt = Math.min(previousIndex, next.length)
        next.splice(insertAt === -1 ? next.length : insertAt, 0, taskToDelete)
        return next
      })
      alert('Failed to delete task. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={showDeleteConfirm}
      onClose={() => setShowDeleteConfirm(false)}
    >
      <DialogTitle>Delete Task</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to delete this task? This action cannot be
          undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
        <Button variant='contained' color='error' onClick={handleDeleteTask}>
          {loading ? (
            <CircularProgress size={20} color='secondary' />
          ) : (
            'Delete'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
